/**
 * Judge ordering utility.
 *
 * Judges have a `sort_order` (int) that controls how they appear
 * left-to-right on the public Display (and everywhere else that
 * lists judges). Assignment lists (`assigned_judge_numbers` on a
 * tournament) are stored as a SET — the order in that array
 * carries no meaning on its own.
 *
 * This helper turns a raw list of judge_numbers into the
 * ordered list expected by the UI, using the `sort_order` from
 * the loaded judges collection.
 *
 * IMPORTANT — filtering rule:
 *   When `judgesList` is populated, we DROP any judge_number that
 *   has no corresponding judge in the event. Public displays must
 *   reflect the real roster: if the event only has 3 judges we
 *   render 3 slots, never phantom "JUDGE 4"/"JUDGE 5" placeholders.
 *   If `judgesList` is empty (typically the very first render
 *   before judges have loaded), we keep the numbers as-is to avoid
 *   momentarily hiding all judges.
 */
export function sortJudgeNumbers(numbers, judgesList) {
  if (!Array.isArray(numbers)) return [];
  if (!Array.isArray(judgesList) || judgesList.length === 0) {
    return [...numbers];
  }
  const byNum = new Map();
  judgesList.forEach((j) => {
    if (j && typeof j.judge_number !== "undefined") {
      byNum.set(j.judge_number, j);
    }
  });
  return [...numbers]
    // Drop judge_numbers that don't map to a real judge in the event.
    .filter((n) => byNum.has(n))
    .sort((a, b) => {
      const ja = byNum.get(a);
      const jb = byNum.get(b);
      const oa = ja && typeof ja.sort_order === "number" ? ja.sort_order : a;
      const ob = jb && typeof jb.sort_order === "number" ? jb.sort_order : b;
      if (oa !== ob) return oa - ob;
      return a - b;
    });
}

export default sortJudgeNumbers;
